/**
 * RiderPreDetailModal Component
 * 라이더 선정산 상세 모달
 */
import React from "react";
import { RiderPreSettlement } from "../types/riderPreSettlement.types";
import { formatCurrency } from "../utils/riderPreSettlementHelpers";
import '../styles/RiderPreSettlementHistory.css';

interface RiderPreDetailModalProps {
    settlement: RiderPreSettlement;
    onClose: () => void;
    isRiderUser: boolean;
}

const RiderPreDetailModal: React.FC<RiderPreDetailModalProps> = ({
    settlement,
    onClose,
    isRiderUser
}) => {
    /** 배경 클릭 시 닫기 */
    const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className="rider-pre-detail-modal-overlay" onClick={handleBackdropClick}>
            <div className="rider-pre-detail-modal" onClick={(e) => e.stopPropagation()}>
                {/* 헤더 */}
                <div className="rider-pre-detail-modal-header">
                    <div>
                        <div className="subtitle">라이더 선정산 상세</div>
                        <h2>{settlement.riderName} ({settlement.riderId})</h2>
                    </div>
                    <button className="rider-pre-detail-modal-close" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                {/* 본문 */}
                <div className="rider-pre-detail-modal-body">
                    {/* 기본 정보 */}
                    <div className="rider-pre-detail-section">
                        <div className="rider-pre-detail-section-title">
                            <i className="fas fa-user" style={{ marginRight: '8px' }}></i>
                            기본 정보
                        </div>
                        <div className="rider-pre-detail-grid">
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">협력사명</span>
                                <span className="rider-pre-detail-value">{settlement.supplierName}</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">라이더 ID</span>
                                <span className="rider-pre-detail-value">{settlement.riderId}</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">라이더명</span>
                                <span className="rider-pre-detail-value">{settlement.riderName}</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">라이더 구분</span>
                                <span className="rider-pre-detail-value">{settlement.riderType}</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">배송일자</span>
                                <span className="rider-pre-detail-value">{settlement.deliveryDate}</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">지급일</span>
                                <span className="rider-pre-detail-value">{settlement.paymentDate}</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">처리 건수</span>
                                <span className="rider-pre-detail-value">{formatCurrency(settlement.processCount)}건</span>
                            </div>
                        </div>
                    </div>

                    {/* 배달료 정보 */}
                    <div className="rider-pre-detail-section">
                        <div className="rider-pre-detail-section-title">
                            <i className="fas fa-motorcycle" style={{ marginRight: '8px' }}></i>
                            배달료 정보
                        </div>
                        <div className="rider-pre-detail-grid">
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">배달료</span>
                                <span className="rider-pre-detail-value">{formatCurrency(settlement.deliveryFee)}원</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">과세배달료</span>
                                <span className="rider-pre-detail-value">{formatCurrency(settlement.taxableDeliveryFee)}원</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">월보수액</span>
                                <span className="rider-pre-detail-value">{formatCurrency(settlement.monthlySalary)}원</span>
                            </div>
                        </div>
                    </div>

                    {/* 수수료 정보 (라이더가 아닌 경우만 표시) */}
                    {!isRiderUser && (
                        <div className="rider-pre-detail-section">
                            <div className="rider-pre-detail-section-title">
                                <i className="fas fa-percentage" style={{ marginRight: '8px' }}></i>
                                수수료 정보
                            </div>
                            <div className="rider-pre-detail-grid">
                                <div className="rider-pre-detail-item">
                                    <span className="rider-pre-detail-label">중개사 수수료</span>
                                    <span className="rider-pre-detail-value negative">{formatCurrency(settlement.brokerManageFee)}원</span>
                                </div>
                                <div className="rider-pre-detail-item">
                                    <span className="rider-pre-detail-label">협력사 수수료</span>
                                    <span className="rider-pre-detail-value negative">{formatCurrency(settlement.partnerOperationFee)}원</span>
                                </div>
                                <div className="rider-pre-detail-item">
                                    <span className="rider-pre-detail-label">총 운영수수료</span>
                                    <span className="rider-pre-detail-value negative">{formatCurrency(settlement.totalOperationFee)}원</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* 보험료 정보 */}
                    <div className="rider-pre-detail-section">
                        <div className="rider-pre-detail-section-title">
                            <i className="fas fa-shield-alt" style={{ marginRight: '8px' }}></i>
                            보험료 정보
                        </div>
                        <div className="rider-pre-detail-grid">
                            {!isRiderUser && (
                                <div className="rider-pre-detail-item">
                                    <span className="rider-pre-detail-label">라이더 고용보험</span>
                                    <span className="rider-pre-detail-value negative">{formatCurrency(settlement.riderEmployment)}원</span>
                                </div>
                            )}
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">라이더 산재보험</span>
                                <span className="rider-pre-detail-value negative">{formatCurrency(settlement.riderIndustrial)}원</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">시간제 보험료</span>
                                <span className="rider-pre-detail-value negative">{formatCurrency(settlement.hourlyInsurance)}원</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">보험료 총합</span>
                                <span className="rider-pre-detail-value negative">{formatCurrency(settlement.insuranceTotal)}원</span>
                            </div>
                        </div>
                    </div>

                    {/* 세금 정보 */}
                    <div className="rider-pre-detail-section">
                        <div className="rider-pre-detail-section-title">
                            <i className="fas fa-file-invoice-dollar" style={{ marginRight: '8px' }}></i>
                            세금 정보
                        </div>
                        <div className="rider-pre-detail-grid">
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">소득세</span>
                                <span className="rider-pre-detail-value negative">{formatCurrency(settlement.incomeTax)}원</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">지방세</span>
                                <span className="rider-pre-detail-value negative">{formatCurrency(settlement.localTax)}원</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">원천징수</span>
                                <span className="rider-pre-detail-value negative">{formatCurrency(settlement.withholdingTax)}원</span>
                            </div>
                        </div>
                    </div>

                    {/* 정산 정보 */}
                    <div className="rider-pre-detail-section">
                        <div className="rider-pre-detail-section-title">
                            <i className="fas fa-calculator" style={{ marginRight: '8px' }}></i>
                            정산 정보
                        </div>
                        <div className="rider-pre-detail-grid">
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">본사 일차감</span>
                                <span className="rider-pre-detail-value negative">{formatCurrency(settlement.hdcDeductVal)}원</span>
                            </div>
                            <div className="rider-pre-detail-item">
                                <span className="rider-pre-detail-label">협력사 일차감</span>
                                <span className="rider-pre-detail-value negative">{formatCurrency(settlement.sdcDeductVal)}원</span>
                            </div>
                            <div className="rider-pre-detail-item highlight">
                                <span className="rider-pre-detail-label">선지급 배달료</span>
                                <span className="rider-pre-detail-value amount">{formatCurrency(settlement.advanceSettlement)}원</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 푸터 */}
                <div className="rider-pre-detail-modal-footer">
                    <button onClick={onClose}>
                        <i className="fas fa-times" style={{ marginRight: '8px' }}></i>
                        닫기
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RiderPreDetailModal;
